import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react";
import { UserPayload } from "../models/SiteModel";

// Define context value type
export interface UserContextType {
  user: UserPayload | undefined;
  setUser: (user: UserPayload | undefined) => void;
  isLoggedIn: () => boolean;
  getUser: () => UserPayload | undefined;
}

// Create context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Custom hook to access the user context
export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

// UserProvider component to provide context value to the app
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<UserPayload | undefined>(() => {
    const savedUser = sessionStorage.getItem("user");
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch {
        sessionStorage.removeItem("user");
      }
    }

    return undefined;
  });

  const setUser = useCallback((user: UserPayload | undefined) => {
    setUserState(user);
    if (user) {
      sessionStorage.setItem("user", JSON.stringify(user)); // Save user to sessionStorage
    } else {
      sessionStorage.removeItem("user"); // Remove user from sessionStorage if logged out
    }
  }, []);

  const getUser = useCallback(() => {
    return user;
  }, [user]);

  const isLoggedIn = useCallback(() =>{
    return !!user;
  }, [user]);

  const contextValue = useMemo(
    () => ({ user, setUser, isLoggedIn, getUser }),
    [user, setUser, isLoggedIn, getUser]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};
